import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserMaintenanceRoutes } from 'src/app/core/enums/routes.enum';
import { isCorrectiveActionKind } from 'src/app/maintenance-user/functions/is-corrective-action-kind';
import { hasCorrectiveActionPerforms } from 'src/app/maintenance-user/functions/has-corrective-action-performs';
import { getMaintenanceReferrerComponentId } from 'src/app/maintenance-user/functions/get-maintenance-referrer-component-id';
import { IMaintenanceJobForTable } from 'src/app/maintenance-user/models/maintenance-job.model';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  ReactivateMaintenanceJob,
  RestoreMaintenanceJob,
} from 'src/app/maintenance-user/state/maintenance-job.actions';
import { Privileges } from 'src/app/core/privileges/privileges';

@Component({
  selector: 'app-maintenance-template',
  templateUrl: './maintenance-template.component.html',
  styleUrls: ['./maintenance-template.component.scss'],
  standalone: false,
})
export class MaintenanceTemplateComponent implements OnInit {
  @Input() columnName: string;
  @Input() element: IMaintenanceJobForTable & Record<string, any>;
  @Input() buttonFunctions: { [key: string]: () => void };

  Privileges = Privileges;
  expiryDate: Date | undefined;
  performRoute: string;
  marineManagerEnabled = this.store.selectSnapshot(TenantUserAccessState.getMarineManagerEnabled);

  get oneTimeDueDate(): string | null | undefined {
    return (
      this.element?.dueDateOrRuntime?.oneTimeExpiry ?? this.element?.expiry?.oneTimeExpiry?.dueDate
    );
  }

  get intervalDueDate(): string | null | undefined {
    return this.element?.dueDateOrRuntime?.intervalExpiry;
  }

  get runtimeDueHours(): number | null | undefined {
    return this.element?.dueDateOrRuntime?.runtimeExpiry?.hours;
  }

  get intervalDays(): number | null | undefined {
    return this.element?.interval?.intervalExpiry;
  }

  get runtimeHours(): number | null | undefined {
    return this.element?.interval?.runtimeExpiry;
  }

  get intervalTimeUntilExpiry(): number | null | undefined {
    return this.element?.timeUntilExpiry?.intervalExpiry;
  }

  get runtimeTimeUntilExpiry(): number | null | undefined {
    return this.element?.timeUntilExpiry?.runtimeExpiry?.timeUntilExpiry;
  }

  get runtimeAvgDailyRuntime(): number | null | undefined {
    return this.element?.timeUntilExpiry?.runtimeExpiry?.avgDailyRuntime;
  }

  get isOneTimeExpiryAndRuntime(): boolean {
    return this.element?.expiryType === 'OneTimeExpiryAndRuntime';
  }

  get isOneTimeExpiryType(): boolean {
    return this.element?.expiryType === 'OneTimeExpiry' || this.isOneTimeExpiryAndRuntime;
  }

  get isCombinedExpiryType(): boolean {
    return this.element?.expiryType === 'Both' || this.isOneTimeExpiryAndRuntime;
  }

  get hasRuntimeExpiryDisplay(): boolean {
    return (
      this.element?.expiryType === 'RuntimeExpiry' ||
      this.element?.expiryType === 'Both' ||
      this.isOneTimeExpiryAndRuntime
    );
  }

  get oneTimeOrIntervalDueDate(): string | null | undefined {
    return this.oneTimeDueDate ?? this.intervalDueDate;
  }

  get isProjectExportedActionsDisabled(): boolean {
    return this.marineManagerEnabled && (this.element?.project?.isExported ?? false);
  }

  get performButtonDisabled(): boolean {
    return (
      this.element.isDisabled ||
      (!this.element.canPerform && !this.element.canOpenDraft) ||
      (!this.element.isDraft && this.isProjectExportedActionsDisabled)
    );
  }

  get performButtonTooltip(): string {
    if (!this.element.isDraft && this.isProjectExportedActionsDisabled) {
      return 'projectExportedActionsDisabled';
    }
    if ((!this.element.canPerform && !this.element.canOpenDraft) || this.element.isDisabled) {
      return 'notResponsibleForPerformingJob';
    }
    return '';
  }

  get isCorrectiveActionPerform(): boolean {
    return isCorrectiveActionKind(this.element.kind);
  }

  get showPerformButton(): boolean {
    if (!this.isCorrectiveActionPerform) {
      return true;
    }

    return !hasCorrectiveActionPerforms(this.element);
  }

  get performRouteQueryParams(): Params {
    const { componentId } = this.route.snapshot.params;
    return {
      ...(this.isCorrectiveActionPerform
        ? { draft: this.element.isDraft ? 'true' : null }
        : { kind: this.element.kind }),
      referrerComponentId: getMaintenanceReferrerComponentId(componentId, this.element.menuId),
    };
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const runtimeDueDate = this.element?.dueDateOrRuntime?.runtimeExpiry?.dueDate;

    if (this.isCombinedExpiryType) {
      const intervalDate = this.isOneTimeExpiryAndRuntime
        ? this.oneTimeOrIntervalDueDate
        : this.intervalDueDate;

      if (intervalDate && runtimeDueDate) {
        this.expiryDate = new Date(Math.min(+new Date(intervalDate), +new Date(runtimeDueDate)));
      }
    }
    if (isCorrectiveActionKind(this.element.kind)) {
      this.performRoute = UserMaintenanceRoutes.CorrectiveActionPerform.replace(
        ':componentId',
        this.element.menuId
      ).replace(':caId', this.element.jobId);
    } else {
      this.performRoute = UserMaintenanceRoutes.JobPerform.replace(
        ':componentId',
        this.element.menuId
      ).replace(':id', this.element.jobId);
    }
  }

  restore(): void {
    this.store.dispatch(new RestoreMaintenanceJob(this.element.jobId));
  }

  reactivate(): void {
    this.store.dispatch(new ReactivateMaintenanceJob(this.element.jobId));
  }
}
